import React, { useState } from 'react';
import {
  Alert,
  Card,
  Col,
  Form,
  InputNumber,
  Row,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { FormRule } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { MatchdayDirectorConfigPayload } from '@/types';
import {
  GDD_DEFAULT_AVERAGE_ROWS,
  GDD_TIERS,
  finiteNumber,
  formatNumber,
  formatRange,
  isUsableAverage,
  maxDifficultyFactor,
  previewRange,
  tierCondition,
  emptyTiers,
  tierRequirement,
  unreachableTiers,
} from './gddPreview';
import type {
  GddPreviewRange,
  GddRangesDraft,
  GddSide,
  GddThresholdsDraft,
  GddTierInfo,
} from './gddPreview';

const { Text } = Typography;

type FieldPath = string[];

/** A form path inside the config's `gddRanges` section. */
const gddPath = (...keys: string[]): FieldPath => ['gddRanges', ...keys];

const THRESHOLD_PATHS = {
  mediumFrom: gddPath('tierThresholds', 'mediumFrom'),
  hardAbove: gddPath('tierThresholds', 'hardAbove'),
  veryHardAbove: gddPath('tierThresholds', 'veryHardAbove'),
};
const AVERAGE_PATHS = {
  minBalls: gddPath('averages', 'minBalls'),
  min: gddPath('averages', 'min'),
  max: gddPath('averages', 'max'),
};
const LIMIT_PATHS = {
  floor: gddPath('rangeLimits', 'floor'),
  minWidth: gddPath('rangeLimits', 'minWidth'),
  cap: gddPath('rangeLimits', 'cap'),
};

/** Float slack for floor + min width ≤ cap (0.1 + 0.2 is more than 0.3 in floating point). */
const EPSILON = 1e-9;

const SIDE_LABELS: Record<GddSide, string> = { runRate: 'run rate', economy: 'economy' };

/** The GDD worked example: a player averaging 12.70 runs per over batting and 5.67 bowling. */
const SAMPLE_AVERAGES: Record<GddSide, number | null> = { runRate: 12.7, economy: 5.67 };

// ---- Rules. Every box is required: InputNumber gives null for an emptied box. ----

const required: FormRule = { required: true, message: 'Required' };

const within = (min: number, max: number): FormRule => ({
  type: 'number',
  min,
  max,
  message: `Must be from ${min} to ${max}`,
});

const aboveZero: FormRule = {
  validator: (_rule, value: unknown) => {
    const n = finiteNumber(value);
    return n === null || n > 0 ? Promise.resolve() : Promise.reject(new Error('Must be above 0'));
  },
};

/** Checks this box against the one at `otherPath`. An empty box is left to `required`. */
const compareWith =
  (
    otherPath: FieldPath,
    ok: (value: number, other: number) => boolean,
    message: string
  ): FormRule =>
  ({ getFieldValue }) => ({
    validator: (_rule, value: unknown) => {
      const own = finiteNumber(value);
      const other = finiteNumber(getFieldValue(otherPath));
      return own === null || other === null || ok(own, other)
        ? Promise.resolve()
        : Promise.reject(new Error(message));
    },
  });

/** floor + min width ≤ cap. On all three boxes, so the one being edited shows it. */
const limitsFit: FormRule = ({ getFieldValue }) => ({
  validator: () => {
    const floor = finiteNumber(getFieldValue(LIMIT_PATHS.floor));
    const minWidth = finiteNumber(getFieldValue(LIMIT_PATHS.minWidth));
    const cap = finiteNumber(getFieldValue(LIMIT_PATHS.cap));
    return floor === null || minWidth === null || cap === null || floor + minWidth <= cap + EPSILON
      ? Promise.resolve()
      : Promise.reject(new Error('Floor + min width must be ≤ cap'));
  },
});

/** A live form value for help text and the preview. Any part may be missing or null mid-edit. */
const useLiveValue = <T,>(path: FieldPath): T | undefined => {
  const form = Form.useFormInstance<MatchdayDirectorConfigPayload>();
  return Form.useWatch(path, { form, preserve: true });
};

/** "[8.70, 10.70]", tagged when the range limits moved it; "—" while a value is empty. */
const RangeText: React.FC<{ value: GddPreviewRange | null }> = ({ value }) => {
  if (!value) return <Text type="secondary">—</Text>;
  return (
    <span style={{ whiteSpace: 'nowrap' }}>
      <Text code>{formatRange(value.range)}</Text>
      {value.limited && (
        <Tooltip title={`Average + band is ${formatRange(value.raw)}; the range limits moved it.`}>
          <Tag style={{ marginLeft: 6 }}>limited</Tag>
        </Tooltip>
      )}
    </span>
  );
};

const TierThresholdsCard: React.FC = () => {
  const thresholds = useLiveValue<GddThresholdsDraft>(gddPath('tierThresholds'));
  const baseDifficulty = useLiveValue<unknown>(['difficultyFactor', 'baseDifficulty']);
  const scaleMultiplier = useLiveValue<unknown>(['winLossFactor', 'scaleMultiplier']);
  const maxDF = maxDifficultyFactor(baseDifficulty, scaleMultiplier);
  // A tier with an empty dF interval is reported once, with its own reason;
  // "lower a threshold" only fits tiers above the highest reachable dF.
  const empty = emptyTiers(thresholds);
  const emptyKeys = empty.map((item) => item.tier);
  const unreachable = maxDF === null ? [] : unreachableTiers(thresholds, maxDF);
  const unreachableInfo = GDD_TIERS.filter(
    (tier) => unreachable.includes(tier.key) && !emptyKeys.includes(tier.key)
  );
  const emptyInfo = GDD_TIERS.flatMap((tier) => {
    const item = empty.find((entry) => entry.tier === tier.key);
    return item ? [{ label: tier.label, reason: item.reason }] : [];
  });

  return (
    <Card title="Difficulty tiers (dF thresholds)" style={{ marginBottom: 16 }}>
      <Row gutter={16}>
        <Col xs={24} sm={8}>
          <Form.Item
            name={THRESHOLD_PATHS.mediumFrom}
            label="Medium from (dF ≥)"
            dependencies={[THRESHOLD_PATHS.hardAbove]}
            rules={[
              required,
              within(0, 10),
              compareWith(
                THRESHOLD_PATHS.hardAbove,
                (value, hardAbove) => value <= hardAbove,
                'Must be ≤ Hard above'
              ),
            ]}
          >
            <InputNumber style={{ width: '100%' }} step={0.05} min={0} max={10} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item
            name={THRESHOLD_PATHS.hardAbove}
            label="Hard above (dF >)"
            dependencies={[THRESHOLD_PATHS.mediumFrom, THRESHOLD_PATHS.veryHardAbove]}
            rules={[
              required,
              within(0, 10),
              compareWith(
                THRESHOLD_PATHS.mediumFrom,
                (value, mediumFrom) => value >= mediumFrom,
                'Must be ≥ Medium from'
              ),
              compareWith(
                THRESHOLD_PATHS.veryHardAbove,
                (value, veryHardAbove) => value <= veryHardAbove,
                'Must be ≤ Very Hard above'
              ),
            ]}
          >
            <InputNumber style={{ width: '100%' }} step={0.05} min={0} max={10} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item
            name={THRESHOLD_PATHS.veryHardAbove}
            label="Very Hard above (dF >)"
            dependencies={[THRESHOLD_PATHS.hardAbove]}
            rules={[
              required,
              within(0, 10),
              compareWith(
                THRESHOLD_PATHS.hardAbove,
                (value, hardAbove) => value >= hardAbove,
                'Must be ≥ Hard above'
              ),
            ]}
          >
            <InputNumber style={{ width: '100%' }} step={0.05} min={0} max={10} />
          </Form.Item>
        </Col>
      </Row>
      <ul style={{ margin: '0 0 8px', paddingLeft: 20 }}>
        {GDD_TIERS.map((tier) => (
          <li key={tier.key}>
            <Text strong>{tier.label}</Text>: {tierCondition(tier.key, thresholds)}{' '}
            <Text type="secondary">
              → game difficulty {tier.difficulty} ({tier.difficultyName})
            </Text>
          </li>
        ))}
      </ul>
      <Text type="secondary" style={{ display: 'block' }}>
        dF = baseDifficulty × wLF × (1 − cF × cW), from the factors below. The tier picks the AI
        target band and is sent to the game as <Text code>difficulty</Text>.
      </Text>
      {emptyInfo.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <Text type="warning">
            Never used: {emptyInfo.map((item) => `${item.label} (${item.reason})`).join(', ')}.
          </Text>
        </div>
      )}
      {maxDF !== null && (
        <div style={{ marginTop: 8 }}>
          {unreachableInfo.length > 0 ? (
            <Text type="warning">
              Never reached with these values:{' '}
              {unreachableInfo
                .map((tier) => `${tier.label} (needs ${tierRequirement(tier.key, thresholds)})`)
                .join(', ')}
              . dF is at most {formatNumber(maxDF)}: base difficulty {formatNumber(baseDifficulty)}{' '}
              × win/loss scale multiplier {formatNumber(scaleMultiplier)}, at 100% wins with no
              churn. Lower a threshold below {formatNumber(maxDF)} to use its tier.
            </Text>
          ) : (
            <Text type="secondary">
              dF is at most {formatNumber(maxDF)} (base difficulty × win/loss scale multiplier, at
              100% wins with no churn).
            </Text>
          )}
        </div>
      )}
    </Card>
  );
};

const BAND_COLUMNS: { side: GddSide; bound: 'min' | 'max'; title: string }[] = [
  { side: 'runRate', bound: 'min', title: 'Run rate min' },
  { side: 'runRate', bound: 'max', title: 'Run rate max' },
  { side: 'economy', bound: 'min', title: 'Economy min' },
  { side: 'economy', bound: 'max', title: 'Economy max' },
];

const BandInput: React.FC<{
  tier: GddTierInfo;
  side: GddSide;
  bound: 'min' | 'max';
  title: string;
}> = ({ tier, side, bound, title }) => {
  const otherPath = gddPath('bands', tier.key, side, bound === 'min' ? 'max' : 'min');
  return (
    <Form.Item
      name={gddPath('bands', tier.key, side, bound)}
      dependencies={[otherPath]}
      rules={[
        required,
        within(-20, 20),
        bound === 'min'
          ? compareWith(otherPath, (value, max) => value <= max, 'Must be ≤ max')
          : compareWith(otherPath, (value, min) => value >= min, 'Must be ≥ min'),
      ]}
      style={{ marginBottom: 0 }}
    >
      <InputNumber
        style={{ width: '100%' }}
        step={0.25}
        min={-20}
        max={20}
        aria-label={`${tier.label}: ${title}`}
      />
    </Form.Item>
  );
};

const BandsCard: React.FC = () => {
  const thresholds = useLiveValue<GddThresholdsDraft>(gddPath('tierThresholds'));
  const columns: ColumnsType<GddTierInfo> = [
    {
      title: 'Tier',
      key: 'tier',
      width: 160,
      render: (_, tier) => (
        <>
          <Text strong>{tier.label}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {tierCondition(tier.key, thresholds)}
          </Text>
        </>
      ),
    },
    ...BAND_COLUMNS.map(({ side, bound, title }) => ({
      title,
      key: `${side}-${bound}`,
      render: (_: unknown, tier: GddTierInfo) => (
        <BandInput tier={tier} side={side} bound={bound} title={title} />
      ),
    })),
  ];

  return (
    <Card
      title="AI target bands (added to the player's average, runs per over)"
      style={{ marginBottom: 16 }}
    >
      <Table<GddTierInfo>
        rowKey="key"
        size="small"
        bordered
        pagination={false}
        columns={columns}
        dataSource={GDD_TIERS}
        scroll={{ x: 680 }}
      />
      <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
        The AI’s run-rate target is the player’s own run rate plus the run-rate band; its economy
        target is the player’s own economy plus the economy band. Each min must be ≤ its max, from
        −20 to 20.
      </Text>
    </Card>
  );
};

const PlayerAverageCard: React.FC = () => {
  const averages = useLiveValue<GddRangesDraft['averages']>(gddPath('averages'));
  return (
    <Card title="Player average" style={{ marginBottom: 16 }}>
      <Row gutter={16}>
        <Col xs={24} sm={8}>
          <Form.Item
            name={AVERAGE_PATHS.minBalls}
            label="Minimum balls (per side)"
            rules={[
              required,
              { type: 'integer', min: 0, max: 600, message: 'A whole number from 0 to 600' },
            ]}
          >
            <InputNumber style={{ width: '100%' }} step={1} precision={0} min={0} max={600} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item
            name={AVERAGE_PATHS.min}
            label="Lowest usable average"
            dependencies={[AVERAGE_PATHS.max]}
            rules={[
              required,
              within(0, 100),
              compareWith(
                AVERAGE_PATHS.max,
                (value, max) => value < max,
                'Must be below the highest'
              ),
            ]}
          >
            <InputNumber style={{ width: '100%' }} step={0.5} min={0} max={100} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item
            name={AVERAGE_PATHS.max}
            label="Highest usable average"
            dependencies={[AVERAGE_PATHS.min]}
            rules={[
              required,
              within(0, 100),
              compareWith(
                AVERAGE_PATHS.min,
                (value, min) => value > min,
                'Must be above the lowest'
              ),
            ]}
          >
            <InputNumber style={{ width: '100%' }} step={0.5} min={0} max={100} />
          </Form.Item>
        </Col>
      </Row>
      <Text type="secondary">
        A side’s own average (batting → run rate, bowling → economy) counts when that side has at
        least {formatNumber(averages?.minBalls)} balls and the average is from{' '}
        {formatNumber(averages?.min)} to {formatNumber(averages?.max)} runs per over. Otherwise the
        default average for the match length is used.
      </Text>
    </Card>
  );
};

const RangeLimitsCard: React.FC = () => (
  <Card title="Range limits" style={{ marginBottom: 16 }}>
    <Row gutter={16}>
      <Col xs={24} sm={8}>
        <Form.Item
          name={LIMIT_PATHS.floor}
          label="Floor (lowest min)"
          dependencies={[LIMIT_PATHS.minWidth, LIMIT_PATHS.cap]}
          rules={[required, within(0, 100), aboveZero, limitsFit]}
        >
          <InputNumber style={{ width: '100%' }} step={0.5} min={0} max={100} />
        </Form.Item>
      </Col>
      <Col xs={24} sm={8}>
        <Form.Item
          name={LIMIT_PATHS.minWidth}
          label="Minimum width (max − min)"
          dependencies={[LIMIT_PATHS.floor, LIMIT_PATHS.cap]}
          rules={[required, within(0, 100), limitsFit]}
        >
          <InputNumber style={{ width: '100%' }} step={0.25} min={0} max={100} />
        </Form.Item>
      </Col>
      <Col xs={24} sm={8}>
        <Form.Item
          name={LIMIT_PATHS.cap}
          label="Cap (highest max)"
          dependencies={[LIMIT_PATHS.floor, LIMIT_PATHS.minWidth]}
          rules={[required, within(0, 100), limitsFit]}
        >
          <InputNumber style={{ width: '100%' }} step={0.5} min={0} max={100} />
        </Form.Item>
      </Col>
    </Row>
    <Text type="secondary">
      Every range sent is kept valid: floor ≤ min ≤ cap − min width and min + min width ≤ max ≤ cap,
      rounded to 2 decimals. Floor must be above 0, and floor + min width must be ≤ cap (at most
      100).
    </Text>
  </Card>
);

type DefaultAverageRow = (typeof GDD_DEFAULT_AVERAGE_ROWS)[number];

const DefaultAverageInput: React.FC<{ row: DefaultAverageRow; side: GddSide }> = ({
  row,
  side,
}) => (
  <Form.Item
    name={gddPath('defaultAverages', row.key, side)}
    rules={[required, within(0, 100), aboveZero]}
    style={{ marginBottom: 0 }}
  >
    <InputNumber
      style={{ width: '100%' }}
      step={0.1}
      min={0}
      max={100}
      aria-label={`${row.label}: default ${SIDE_LABELS[side]}`}
    />
  </Form.Item>
);

const DEFAULT_AVERAGE_COLUMNS: ColumnsType<DefaultAverageRow> = [
  { title: 'Match length', dataIndex: 'label', key: 'label', width: 240 },
  {
    title: 'Run rate',
    key: 'runRate',
    render: (_, row) => <DefaultAverageInput row={row} side="runRate" />,
  },
  {
    title: 'Economy',
    key: 'economy',
    render: (_, row) => <DefaultAverageInput row={row} side="economy" />,
  },
];

const DefaultAveragesCard: React.FC = () => (
  <Card title="Default averages (players without a usable average)" style={{ marginBottom: 16 }}>
    <Table<DefaultAverageRow>
      rowKey="key"
      size="small"
      bordered
      pagination={false}
      columns={DEFAULT_AVERAGE_COLUMNS}
      dataSource={GDD_DEFAULT_AVERAGE_ROWS}
      scroll={{ x: 520 }}
    />
    <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
      Runs per over, above 0 and at most 100, for a side whose own average doesn’t count: the row
      for the match length, or Fallback for any other length. The in-code defaults (what Restore
      defaults sets) are the production medians of 10 Sep–7 Oct 2026 (per player, 18+ balls); 1 over
      had no data and uses the 3-over values.
    </Text>
  </Card>
);

const PreviewCard: React.FC = () => {
  const gdd = useLiveValue<GddRangesDraft>(gddPath());
  const [sample, setSample] = useState<Record<GddSide, number | null>>(SAMPLE_AVERAGES);
  const thresholds = gdd?.tierThresholds;
  const usable: Record<GddSide, boolean | null> = {
    runRate: isUsableAverage(gdd, sample.runRate),
    economy: isUsableAverage(gdd, sample.economy),
  };
  const defaults3 = gdd?.defaultAverages?.overs3;

  const targetCell = (tier: GddTierInfo, side: GddSide) =>
    usable[side] === false ? (
      <Text type="secondary">Default average used</Text>
    ) : (
      <RangeText value={previewRange(gdd, sample[side], tier.key, side)} />
    );

  const columns: ColumnsType<GddTierInfo> = [
    {
      title: 'Tier',
      key: 'tier',
      render: (_, tier) => (
        <>
          <Text strong>{tier.label}</Text>{' '}
          <Text type="secondary">
            ({tierCondition(tier.key, thresholds)} · difficulty {tier.difficulty})
          </Text>
        </>
      ),
    },
    {
      title: 'AI run-rate target',
      key: 'runRate',
      render: (_, tier) => targetCell(tier, 'runRate'),
    },
    {
      title: 'AI economy target',
      key: 'economy',
      render: (_, tier) => targetCell(tier, 'economy'),
    },
  ];

  const sampleInput = (side: GddSide) => (
    <InputNumber
      // A what-if value, not config: stays editable while the form is read-only.
      disabled={false}
      value={sample[side]}
      onChange={(value) =>
        setSample((prev) => ({ ...prev, [side]: typeof value === 'number' ? value : null }))
      }
      step={0.1}
      min={0}
      max={100}
      precision={2}
      aria-label={`Sample ${SIDE_LABELS[side]}`}
      style={{ width: 110 }}
    />
  );

  return (
    <Card
      title="Preview"
      extra={<Text type="secondary">Not saved</Text>}
      style={{ marginBottom: 16 }}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        A preview of the server rule, recomputed from the values above as you edit them. The server
        is the source of truth.
      </Text>
      <Space wrap align="center" style={{ marginBottom: 8 }}>
        <Text strong>Sample player average</Text>
        <Text>Run rate</Text>
        {sampleInput('runRate')}
        <Text>Economy</Text>
        {sampleInput('economy')}
      </Space>
      <div style={{ marginBottom: 12 }}>
        <Text type="secondary">
          Taken as the player’s own averages, with at least {formatNumber(gdd?.averages?.minBalls)}{' '}
          balls on each side.
        </Text>
        {(['runRate', 'economy'] as const).map((side) =>
          usable[side] === false ? (
            <div key={side}>
              <Text type="warning">
                A {SIDE_LABELS[side]} of {formatNumber(sample[side])} is outside the usable averages
                ({formatNumber(gdd?.averages?.min)} to {formatNumber(gdd?.averages?.max)}
                ), so the server uses the default average for the match length instead.
              </Text>
            </div>
          ) : null
        )}
      </div>
      <Table<GddTierInfo>
        rowKey="key"
        size="small"
        pagination={false}
        columns={columns}
        dataSource={GDD_TIERS}
        scroll={{ x: 560 }}
      />
      <Text style={{ display: 'block', marginTop: 12 }}>
        No usable average, 3-over match, Medium: run rate{' '}
        <RangeText value={previewRange(gdd, defaults3?.runRate, 'medium', 'runRate')} /> (default{' '}
        {formatNumber(defaults3?.runRate)}), economy{' '}
        <RangeText value={previewRange(gdd, defaults3?.economy, 'medium', 'economy')} /> (default{' '}
        {formatNumber(defaults3?.economy)}).
      </Text>
    </Card>
  );
};

/**
 * The GDD AI-range cards of the Matchday-Director page, shown while the
 * `matchday_director_gdd_ranges` flag is on. Rendered inside the page's Form:
 * every box is a Form.Item under ['gddRanges', …], so it goes through the
 * page's Edit / Review / comment flow and is read-only with the rest of the
 * form. Every box is required, and the cross-field rules match the server's
 * validation.
 */
export const GddRangesEditor: React.FC = () => (
  <>
    <Alert
      type="info"
      showIcon
      message="GDD AI ranges are on"
      description={
        <>
          The AI’s run-rate and economy targets are the player’s own average plus the band of their
          difficulty tier, as the GDD defines them, and the <Text code>difficulty</Text> sent to the
          game is that same tier. The legacy run-rate and economy offsets stay saved but are not
          used while the <Text code>matchday_director_gdd_ranges</Text> flag is on.
        </>
      }
      style={{ marginBottom: 16 }}
    />
    <Alert
      type="warning"
      showIcon
      message="The game client keeps a copy of most of these values"
      description="The Unity client's fallback, used when a server range is missing or invalid, has its own copy of the AI target bands, the usable-average window (min and max), the range limits and the default averages. Tell the client team before changing any of them, so the two copies stay the same."
      style={{ marginBottom: 16 }}
    />
    <TierThresholdsCard />
    <BandsCard />
    <PlayerAverageCard />
    <RangeLimitsCard />
    <DefaultAveragesCard />
    <PreviewCard />
  </>
);

export default GddRangesEditor;
